// utils/performanceOptimizations.ts
import { Platform } from 'react-native';

/**
 * Goal:
 * - Keep production noise low on web (log/info/debug), but NEVER hide errors.
 * - Avoid breaking third-party libs that rely on console methods existing.
 * - Allow turning logs back on via EXPO_PUBLIC_DEBUG_LOGS=1 (web).
 */
export const enableProductionOptimizations = () => {
  if (Platform.OS !== 'web' || process.env.NODE_ENV !== 'production') return;
  if (typeof console === 'undefined') return;

  // Optional override: allow logs in production for debugging
  const debugLogsEnabled =
    typeof window !== 'undefined' &&
    (process.env.EXPO_PUBLIC_DEBUG_LOGS === '1' ||
      (window as any).__KURSI_DEBUG_LOGS__ === true);

  if (debugLogsEnabled) return;

  // Keep references to the original functions (in case you want to restore later)
  const original = (console as any).__KURSI_ORIGINAL_CONSOLE__ || {
    log: console.log,
    info: console.info,
    debug: console.debug,
    warn: console.warn,
    error: console.error,
  };

  // Store once
  if (!(console as any).__KURSI_ORIGINAL_CONSOLE__) {
    (console as any).__KURSI_ORIGINAL_CONSOLE__ = original;
  }

  // Silence only noisy channels; keep warn+error visible for production issues.
  console.log = () => {};
  console.info = () => {};
  console.debug = () => {};
  // IMPORTANT: do NOT override console.error
  // Optional: keep warn too (recommended). If you really want to silence warn,
  // do it only after you’re confident you don’t need it.
  // console.warn = () => {};
};

/**
 * requestIdleCallback polyfill
 * - Use native API when available
 * - Fallback to setTimeout with a small delay
 */
export const requestIdleCallback = (callback: () => void, options?: { timeout?: number }) => {
  if (Platform.OS === 'web' && typeof window !== 'undefined' && 'requestIdleCallback' in window) {
    return (window as any).requestIdleCallback(callback, options);
  }
  const t = options?.timeout ?? 1;
  return setTimeout(callback, Math.max(1, t));
};

export const cancelIdleCallback = (id: number) => {
  if (Platform.OS === 'web' && typeof window !== 'undefined' && 'cancelIdleCallback' in window) {
    return (window as any).cancelIdleCallback(id);
  }
  return clearTimeout(id);
};

/**
 * FlatList config
 * Notes:
 * - getItemLayout assumes fixed row height. If your items are NOT fixed height,
 *   this can cause scroll jumps and wrong virtualization.
 * - Keep it conservative unless you’re sure about item sizing.
 */
export const optimizeFlatListConfig = {
  removeClippedSubviews: true,
  maxToRenderPerBatch: 8,
  updateCellsBatchingPeriod: 50,
  initialNumToRender: 10,
  windowSize: 10,
  getItemLayout: (_data: any, index: number) => ({
    length: 100,
    offset: 100 * index,
    index,
  }),
};